<script>
  import FilterControls from "$lib/components/FilterControls/FilterControls.svelte";
  import VizLayout from "$lib/components/VizLayout/VizLayout.svelte";
  import { ColorLegend } from "@urbaninstitute/dataviz-components/maps";
  import { Tilemap } from "@urbaninstitute/dataviz-components/maps";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import { scaleThreshold } from "d3-scale";
  import { jenks } from "simple-statistics";
  import { getName } from "$utils/geoNames.js";
  import { useToolData } from "$utils/data.svelte.js";
  import { MAP_COLORS } from "$utils/colors.js";
  import { formatNumber } from "$utils/formats.js";

  const toolData = useToolData();

  let colorScale = $derived.by(() => {
    if (toolData.mapData.length > 0) {
      let values = toolData.mapData.map((d) => d.value);
      return getJenksScale(values, MAP_COLORS);
    }
    return () => urbanColors.gray;
  });

  /**
   * @param {ColorPalette} colorPalette
   * @param {number[]} domain
   * @param {string[]} colors
   * @returns {import("d3-scale").ScaleThreshold<number, string, string>}
   */
  function getJenksScale(domain, colors) {
    domain.sort();
    const breaks = jenks(domain, colors.length);
    const thresholds = breaks.slice(1, -1);
    const result = /** @type {import("d3-scale").ScaleThreshold<number, string, string>} */ (
      scaleThreshold(thresholds, colors)
    );
    return result;
  }

  let formatValue = $derived((d) => formatNumber(d, toolData.activeFormat));
</script>

<VizLayout>
  {#snippet controls(onClose)}
    <FilterControls filterType="map" {onClose} />
  {/snippet}
  {#snippet viz()}
    {#if toolData.mapData.length > 0}
      <div style="position: relative;">
        <div class="legend">
          <ColorLegend
            scale={colorScale}
            height={16}
            tickFormat={(d) => formatNumber(d, "INT")}
            maxWidth={300}
          />
        </div>
        <Tilemap
          data={toolData.mapData}
          shape="hex"
          fill={(feature) => colorScale(feature.value)}
          stroke={urbanColors.white}
          strokeWidth={2}
          hoverStroke={urbanColors.yellow}
        >
          {#snippet tooltip(props)}
            <div class="tooltip-inner">
              <div class="tooltip-title">
                <strong>{getName(props.map_id)}</strong>
              </div>
              <div class="tooltip-description">
                {toolData.getMetricLabel(toolData.activeMetric)}
              </div>
              <div class="tooltip-value">
                {formatValue(props.value)}
              </div>
            </div>
          {/snippet}
        </Tilemap>
      </div>
    {/if}
  {/snippet}
</VizLayout>

<style>
  .legend {
    max-width: 300px;
    margin-block: var(--spacing-4);
    margin-inline: auto;
  }
  .tooltip-title,
  .tooltip-description {
    margin-bottom: var(--spacing-2);
  }
  .tooltip-description {
    font-style: italic;
  }
  .tooltip-value {
    font-weight: var(--font-weight-bold);
  }
</style>
